import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, formatImageUrl, categoryNames } from '../utils/helpers';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { ShoppingCart, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { addToCart } = useCart();
  const { addToast } = useToast();

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchQuery]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${API_URL}/products`, { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId) => {
    const result = await addToCart(productId, 1);
    if (result.success) {
      addToast('Termék hozzáadva a kosárhoz!', 'success');
    } else {
      addToast(result.error, 'error');
    }
  };

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="section-title text-4xl md:text-5xl mb-4">
            <span>Termékek</span>
          </h1>
          <p className="text-gray-300 text-lg">Fedezd fel varázslatos termékeinket</p>
        </div>

        {/* Search & Filter */}
        <div className="glass rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Keresés..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-lg transition ${
                  selectedCategory === null
                    ? 'bg-yellow-200 text-black font-semibold'
                    : 'bg-white/5 hover:bg-white/10 text-gray-300'
                }`}
              >
                Minden
              </button>
              {Object.entries(categoryNames).map(([key, name]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-lg transition ${
                    selectedCategory === key
                      ? 'bg-yellow-200 text-black font-semibold'
                      : 'bg-white/5 hover:bg-white/10 text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center text-gray-300 py-12">Betöltés...</div>
        ) : products.length === 0 ? (
          <div className="text-center text-gray-300 py-12 glass rounded-2xl">
            <p className="text-xl">Nincs megjeleníthető termék</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} className="glass rounded-2xl overflow-hidden hover:bg-white/5 transition group">
                {/* Product Image */}
                <Link to={`/termekek/${product.id}`} className="block aspect-square overflow-hidden bg-black/30">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={formatImageUrl(product.images[0])}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <span className="text-6xl">✨</span>
                    </div>
                  )}
                </Link>

                {/* Product Info */}
                <div className="p-4">
                  <div className="text-xs text-yellow-200/70 mb-1">
                    {categoryNames[product.category]}
                  </div>
                  <Link to={`/termekek/${product.id}`}>
                    <h3 className="text-lg font-semibold text-white mb-2 hover:text-yellow-200 transition">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-yellow-200">
                      {formatPrice(product.price)}
                    </span>
                    <button
                      onClick={() => handleAddToCart(product.id)}
                      className="bg-yellow-200 text-black p-2 rounded-lg hover:bg-yellow-300 transition"
                    >
                      <ShoppingCart className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
      `}</style>
    </div>
  );
};

export default Products;